import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatDateTime, initializeFormats, getFormatConfig } from '../../../utils/global';
import { useToast } from '../../hooks/useToast';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';
import { useApHub } from '../ApHub/ApHubContext';
import PDFPreviewDialog from '../TreasuryPayment/PDFPreviewDialog';
import {
  fetchInvoiceDraftFileInstances,
  isDraftUploadIgnored,
  parseDraftUploadAdditionalDetails,
} from './draftUploadsApi';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogCloseIconSx,
  harmonateSectionTitleSx,
  harmonateAbortButtonSx,
} from '../../../theme/harmonateStyles';

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

const getTenantId = () => localStorage.getItem('tenant_id');
const getToken = () => localStorage.getItem('token');

const parseAdditionalDetails = parseDraftUploadAdditionalDetails;

const getServiceBadgeSx = (service) => {
  const value = service || '';
  const styles = {
    upload: { bgcolor: ht.secondaryTab, color: ht.text },
    document_classification: { bgcolor: 'rgba(2, 107, 29, 0.12)', color: ht.green },
    extraction: { bgcolor: 'rgba(46, 166, 160, 0.18)', color: ht.text },
    ingest: { bgcolor: 'rgba(1, 48, 98, 0.1)', color: ht.text },
    ingest_callback: { bgcolor: ht.listBg, color: ht.text },
  };
  return styles[value] || { bgcolor: ht.listBg, color: ht.text };
};

const formatProcessStepDisplay = (value) => {
  if (!value) return 'N/A';
  const text = String(value);
  const failedMatch = text.match(/^(.+ failed):/i);
  if (failedMatch) return failedMatch[1];
  return text;
};

const uploadedByDisplay = (row) => {
  const details = parseAdditionalDetails(row) || {};
  return details.uploadedBy || details.emailUser || row?.username || '';
};

/** Draft uploads may store "CODE - Name"; this column shows name only. */

const businessUnitNameOnly = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const sep = ' - ';
  const idx = raw.indexOf(sep);
  if (idx > 0) return raw.slice(idx + sep.length).trim() || raw;
  return raw;
};

const extractAuditErrorMessage = (value) => {
  if (!value) return null;

  const readParsed = (parsed) => {
    if (!parsed || parsed.ingestType !== 'ap_invoice') return null;
    return parsed.error || parsed.errorMessage || parsed.message || null;
  };

  if (typeof value === 'object') {
    return readParsed(value);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
      return readParsed(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  return null;
};

const formatAuditDetails = (value) => {
  if (!value) return 'N/A';
  const errorMessage = extractAuditErrorMessage(value);
  if (errorMessage) return errorMessage;
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const PendingInvoicesTab = ({
  refreshKey = 0,
  onCountChange,
  onFileDeleted,
}) => {
  const { showSuccess, showError } = useToast();
  const { canDelete, canEdit } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);
  const canIgnore = canEdit || canDelete;
  const { selectedBusinessUnitId } = useApHub();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fileInstances, setFileInstances] = useState([]);
  const [search, setSearch] = useState('');
  const [dateTimeFormat, setDateTimeFormat] = useState('MM-DD-YYYY hh:mm:ss a');

  const [auditLogs, setAuditLogs] = useState([]);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [documentDetails, setDocumentDetails] = useState(null);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [queueView, setQueueView] = useState('active');
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const [ignoreSaving, setIgnoreSaving] = useState(false);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const fetchFileInstances = useCallback(async (options = {}) => {
    const { silent = false } = options;
    const tenantId = getTenantId();
    const token = getToken();
    if (!tenantId || !token) {
      if (!silent) setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const sorted = await fetchInvoiceDraftFileInstances(tenantId, token);
      setFileInstances(sorted);
    } catch (err) {
      if (!silent) {
        setError(err.message || 'Failed to load pending invoices');
        showError(err.message || 'Failed to load pending invoices');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    const tenantId = getTenantId();
    if (tenantId) {
      initializeFormats(tenantId).then(() => {
        const config = getFormatConfig();
        if (config.DateTimeFormat) setDateTimeFormat(config.DateTimeFormat);
      });
    }
    fetchFileInstances();
  }, [fetchFileInstances, refreshKey]);

  const scopedRows = useMemo(() => (
    (Array.isArray(fileInstances) ? fileInstances : []).filter((row) => {
      if (!row) return false;
      const details = parseAdditionalDetails(row) || {};
      const buId = details.businessUnitId || '';
      if (selectedBusinessUnitId && buId !== selectedBusinessUnitId) return false;
      return true;
    })
  ), [fileInstances, selectedBusinessUnitId]);

  const activeRows = useMemo(
    () => scopedRows.filter((row) => !isDraftUploadIgnored(row)),
    [scopedRows]
  );
  const ignoredRows = useMemo(
    () => scopedRows.filter((row) => isDraftUploadIgnored(row)),
    [scopedRows]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const source = queueView === 'ignored' ? ignoredRows : activeRows;
    if (!q) return source;
    return source.filter((row) => {
      const details = parseAdditionalDetails(row) || {};
      const buName = (details.businessUnitName || '').toLowerCase();
      return (
        (row.name || '').toLowerCase().includes(q)
        || uploadedByDisplay(row).toLowerCase().includes(q)
        || (row.processStep || '').toLowerCase().includes(q)
        || buName.includes(q)
      );
    });
  }, [activeRows, ignoredRows, queueView, search]);

  useEffect(() => {
    onCountChange?.(activeRows.length);
  }, [activeRows.length, onCountChange]);

  const auditLogColumns = useMemo(() => [
    {
      field: 'createdAt',
      headerName: 'Timestamp',
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : 'N/A'),
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
          Timestamp
        </Typography>
      ),
    },
    {
      field: 'service',
      headerName: 'Service',
      flex: 0.8,
      minWidth: 150,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
          Service
        </Typography>
      ),
      renderCell: (params) => {
        const badge = getServiceBadgeSx(params.value);
        return (
          <Box sx={{
            ...badge,
            px: 1.5,
            py: 0.5,
            borderRadius: 4,
            fontSize: '0.7rem',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            whiteSpace: 'nowrap',
          }}
          >
            {params.value || 'N/A'}
          </Box>
        );
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      minWidth: 120,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
          Action
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ color: 'black', fontSize: '0.75rem', fontWeight: 500, fontFamily: 'monospace' }}>
          {params.value || 'N/A'}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
          Status
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ color: 'black', fontSize: '0.75rem', fontWeight: 500, textTransform: 'uppercase' }}>
          {params.value || 'N/A'}
        </Box>
      ),
    },
    {
      field: 'details',
      headerName: 'Details',
      flex: 2,
      minWidth: 200,
      renderHeader: () => (
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'black' }}>
          Details
        </Typography>
      ),
      renderCell: (params) => {
        const fullText = formatAuditDetails(params.value);
        const preview = fullText.length > 50 ? `${fullText.substring(0, 50)}...` : fullText;
        return (
          <Tooltip title={fullText} arrow placement="top">
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: 'black',
                fontSize: '0.8rem',
                fontFamily: 'monospace',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                  textDecorationColor: 'text.secondary',
                },
              }}
            >
              {preview}
            </Typography>
          </Tooltip>
        );
      },
    },
  ], []);

  const auditLogGridSx = useMemo(() => ({
    ...harmonateDataGridSx,
    '& .MuiDataGrid-columnHeaders': {
      minHeight: '40px !important',
      maxHeight: '40px !important',
    },
    '& .MuiDataGrid-columnHeader': { py: 1 },
    '& .MuiDataGrid-cell': {
      fontSize: '0.8125rem',
      py: 1,
    },
    '& .MuiDataGrid-footerContainer': {
      minHeight: '48px',
    },
  }), []);

  const handlePreviewClick = (event, row) => {
    event.stopPropagation();
    setPreviewFile(row);
    setPdfPreviewOpen(true);
  };

  const handleClosePdfPreview = () => {
    setPdfPreviewOpen(false);
    setPreviewFile(null);
  };

  const handleInfoClick = async (event, row) => {
    event.stopPropagation();
    try {
      setLogsLoading(true);
      const token = getToken();
      const tenantId = getTenantId();
      if (!token || !tenantId) throw new Error('Authentication required');

      const response = await fetch(
        `${BACKEND_BASE}conductor/file-instances/${row.id}/with-audit-logs?tenantId=${tenantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch audit logs: ${response.status}`);
      }

      const data = await response.json();
      setDocumentDetails(data.document);
      setAuditLogs(data.auditLogs || []);
      setIsLogsModalOpen(true);
    } catch {
      showError('Failed to load document details and audit logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleDeleteClick = (event, row) => {
    if (!canDelete) return;
    event.stopPropagation();
    setFileToDelete(row);
    setDeleteConfirmOpen(true);
  };

  const currentUserName = () => {
    const first = (localStorage.getItem('first_name') || '').trim();
    const last = (localStorage.getItem('last_name') || '').trim();
    const fullName = `${first} ${last}`.trim();
    return fullName || localStorage.getItem('username') || localStorage.getItem('email') || '';
  };

  const handleIgnoreConfirm = async () => {
    if (!canIgnore || !ignoreTarget?.id) return;
    setIgnoreSaving(true);
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');
      const ignoring = queueView !== 'ignored';
      const ignoredBy = encodeURIComponent(currentUserName());
      const path = ignoring
        ? `${BACKEND_BASE}conductor/file-instances/${ignoreTarget.id}/ignore?ignoredBy=${ignoredBy}`
        : `${BACKEND_BASE}conductor/file-instances/${ignoreTarget.id}/unignore`;
      const response = await fetch(path, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        let message = `Failed to ${ignoring ? 'ignore' : 'restore'} file (${response.status})`;
        try {
          const body = await response.json();
          message = body.error || body.message || message;
        } catch { /* ignore */ }
        throw new Error(message);
      }
      showSuccess(ignoring ? 'File moved to Ignored' : 'File restored to the queue');
      setIgnoreTarget(null);
      fetchFileInstances();
    } catch (err) {
      showError(err.message || 'Failed to update ignored status');
    } finally {
      setIgnoreSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete) return;
    if (!fileToDelete?.id) {
      showError('File ID not found');
      return;
    }
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(
        `${BACKEND_BASE}conductor/file-instances/${fileToDelete.id}/delete`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        let message = `Failed to delete file (${response.status})`;
        const errorText = await response.text();
        if (errorText) {
          try {
            const body = JSON.parse(errorText);
            message = body.error || body.message || errorText;
          } catch {
            message = errorText;
          }
        }
        throw new Error(message);
      }

      showSuccess('File removed. The invoice number can be uploaded again.');
      setDeleteConfirmOpen(false);
      setFileToDelete(null);
      fetchFileInstances();
      onFileDeleted?.();
    } catch (err) {
      showError(err.message || 'Failed to delete file');
    }
  };

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: 'File Name',
      flex: 1.2,
      minWidth: 180,
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 1,
      minWidth: 160,
      valueFormatter: (params) => (params.value ? formatDateTime(params.value, dateTimeFormat) : 'N/A'),
    },
    {
      field: 'businessUnitName',
      headerName: 'Business Unit',
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => businessUnitNameOnly(parseAdditionalDetails(params.row).businessUnitName),
    },
    {
      field: 'username',
      headerName: 'Uploaded By',
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => uploadedByDisplay(params.row),
      valueFormatter: (params) => params.value || 'N/A',
    },
    {
      field: 'processStep',
      headerName: 'Process Step',
      flex: 1,
      minWidth: 130,
      valueFormatter: (params) => formatProcessStepDisplay(params.value),
    },
    {
      field: 'preview',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Preview uploaded file">
          <IconButton
            size="small"
            onClick={(e) => handlePreviewClick(e, params.row)}
            sx={{ color: ht.text }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'info',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View Document Details & Audit Logs">
          <IconButton size="small" onClick={(e) => handleInfoClick(e, params.row)} sx={{ color: ht.green }}>
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    ...(queueView === 'active' && canIgnore ? [{
      field: 'ignore',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Ignore file">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIgnoreTarget(params.row);
            }}
            sx={{ color: ht.text }}
          >
            <BlockIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }] : []),
    ...(queueView === 'ignored' && canIgnore ? [{
      field: 'unignore',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Restore to queue">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIgnoreTarget(params.row);
            }}
            sx={{ color: ht.green }}
          >
            <UndoIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }] : []),
    ...(queueView === 'active' && canDelete ? [{
      field: 'delete',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Delete file">
          <IconButton size="small" onClick={(e) => handleDeleteClick(e, params.row)} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }] : []),
  ], [dateTimeFormat, canDelete, canIgnore, queueView]);

  const hasSelectedBusinessUnit = Boolean(selectedBusinessUnitId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ ...harmonateSectionHeaderSx, flexWrap: 'wrap', gap: 2, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.text }}>
            {queueView === 'ignored' ? 'Ignored' : 'Draft'} ({filteredRows.length})
          </Typography>
          <Button
            size="small"
            variant={queueView === 'active' ? 'contained' : 'outlined'}
            onClick={() => setQueueView('active')}
            sx={queueView === 'active' ? harmonateProceedButtonSx : harmonateCancelButtonSx}
          >
            Active ({activeRows.length})
          </Button>
          <Button
            size="small"
            variant={queueView === 'ignored' ? 'contained' : 'outlined'}
            onClick={() => setQueueView('ignored')}
            sx={queueView === 'ignored' ? harmonateProceedButtonSx : harmonateCancelButtonSx}
          >
            Ignored ({ignoredRows.length})
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search file, business unit, or user"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 260, bgcolor: ht.white }}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchFileInstances} disabled={loading} size="small" aria-label="Refresh" sx={{ color: ht.text }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {!hasSelectedBusinessUnit ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
            <Typography variant="body2">Select a business unit on the Dashboard to view draft uploads.</Typography>
          </Box>
        ) : loading && fileInstances.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGridPro
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row.id || row.fileInstanceId || `${row.name || 'file'}-${row.createdAt || ''}`}
            rowHeight={44}
            disableRowSelectionOnClick
            autoHeight={false}
            pagination
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{ ...harmonateDataGridSx, height: '100%' }}
          />
        )}
      </Box>

      {isLogsModalOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1300,
          }}
          onClick={() => setIsLogsModalOpen(false)}
        >
          <Paper
            onClick={(e) => e.stopPropagation()}
            sx={{
              width: '95%',
              maxWidth: '1200px',
              height: '90vh',
              display: 'flex',
              flexDirection: 'column',
              p: 0,
              overflow: 'hidden',
              borderRadius: ht.cardRadius,
              boxShadow: ht.cardShadow,
              bgcolor: ht.white,
            }}
          >
            <Box
              sx={{
                ...harmonateDialogTitleSx,
                px: 2.5,
                py: 2,
                borderRadius: 0,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ht.white }}>
                Document Details & Audit Logs
              </Typography>
              <IconButton size="small" onClick={() => setIsLogsModalOpen(false)} sx={harmonateDialogCloseIconSx}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {logsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
              </Box>
            ) : (
              <Box sx={{ p: 2, overflow: 'auto', flex: 1, bgcolor: ht.listBg }}>
                {documentDetails && (
                  <Box sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: ht.white,
                    borderRadius: ht.cardRadius,
                    border: `1px solid ${ht.line}`,
                  }}
                  >
                    <Box sx={{
                      mb: 1.5,
                      pb: 1,
                      borderBottom: `1px solid ${ht.line}`,
                    }}
                    >
                      <Typography variant="subtitle2" sx={harmonateSectionTitleSx}>
                        DOCUMENT INFORMATION
                      </Typography>
                    </Box>
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                      gap: 2,
                      '& > div': { minWidth: 0 },
                    }}
                    >
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Document Name</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.name || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Document Type</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.documentTypeId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Uploaded At</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>
                          {documentDetails.uploadAt ? new Date(documentDetails.uploadAt).toLocaleString() : 'N/A'}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Location</Typography>
                        <Tooltip title={documentDetails.location || 'N/A'} arrow placement="top">
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              maxWidth: '300px',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                              display: 'inline-block',
                              cursor: 'pointer',
                              color: ht.text,
                              '&:hover': { textDecoration: 'underline', color: ht.text },
                            }}
                          >
                            {documentDetails.location || 'N/A'}
                          </Typography>
                        </Tooltip>
                      </div>
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Unit of Work</Typography>
                        <Typography variant="body2" sx={{ color: ht.text }}>{documentDetails.unitOfWorkId || 'N/A'}</Typography>
                      </div>
                      <div>
                        <Typography variant="caption" sx={{ color: ht.fadedText }} display="block">Integration ID</Typography>
                        <Typography variant="body2" noWrap sx={{ color: ht.text }}>{documentDetails.integrationId || 'N/A'}</Typography>
                      </div>
                    </Box>
                  </Box>
                )}

                <Box sx={{
                  mb: 1.5,
                  pb: 1,
                  borderBottom: `1px solid ${ht.line}`,
                }}
                >
                  <Typography variant="subtitle2" sx={harmonateSectionTitleSx}>
                    AUDIT LOGS
                  </Typography>
                </Box>
                {auditLogs.length > 0 ? (
                  <DataGridPro
                    rows={auditLogs}
                    getRowId={(row) => row.id}
                    columns={auditLogColumns}
                    autoHeight
                    pagination
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooterSelectedRowCount
                    sx={auditLogGridSx}
                  />
                ) : (
                  <Typography sx={{ color: ht.fadedText }}>No audit logs available</Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>
      )}

      <Dialog
        open={Boolean(ignoreTarget)}
        onClose={() => !ignoreSaving && setIgnoreTarget(null)}
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>
          {queueView === 'ignored' ? 'Restore file?' : 'Ignore file?'}
        </DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            {queueView === 'ignored'
              ? `Restore "${ignoreTarget?.name}" to the active queue?`
              : `Move "${ignoreTarget?.name}" to Ignored? The file and its metadata stay available and can be restored.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setIgnoreTarget(null)} variant="outlined" disabled={ignoreSaving} sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleIgnoreConfirm}
            variant="contained"
            disabled={ignoreSaving}
            sx={harmonateProceedButtonSx}
          >
            {ignoreSaving ? 'Saving...' : (queueView === 'ignored' ? 'Restore' : 'Ignore')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Delete file?</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ fontSize: '0.875rem', color: ht.text }}>
            Delete &quot;{fileToDelete?.name}&quot;? It leaves this queue, and the parsed invoice is removed so the same invoice number can be uploaded again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={harmonateAbortButtonSx}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <PDFPreviewDialog
        open={pdfPreviewOpen}
        onClose={handleClosePdfPreview}
        fileId={previewFile?.id}
        tenantId={getTenantId()}
      />
    </Box>
  );
};

export default PendingInvoicesTab;
